// 10-demo runtime for the SoftBank/KDDI side-by-side corp demo.
//
// Each demo is one ``DemoScript`` — a high-level spec compiled into
// timed ``DemoFrame``s. The frames stream into the same reducer that
// the live unified WS feeds (chat surface + AgentGraphPanel + PACT
// log + receipt drawer), so the visual is identical to a live run.
//
// Carrier swap: every script declares ``carrierFit`` (softbank | kddi
// | both). At play-time, ``compileDemoScript()`` substitutes
// carrier-aware strings (settlement brand, denomination, tx_ref
// prefix, loyalty program) so one base script renders correctly
// for either carrier.

import type {
  GraphEdgeEvent,
  GraphNodeEvent,
  NodeId,
  NodeState,
  PactEvent,
  UnifiedEvent,
} from "./agent-graph-types";


export type CarrierId = "paypay_softbank" | "kddi_au";

export const CARRIERS: Record<CarrierId, CarrierProfile> = {
  paypay_softbank: {
    id: "paypay_softbank",
    displayName: "SoftBank Lifestyle",
    settlementBrand: "PayPay",
    settlementAdapterId: "paypay_402",
    walletAdapterLabel: "PayPay Wallet",
    cryptoExchange: "",
    txRefPrefix: "paypay",
    primaryDenomination: "YEN",
    supportedDenominations: ["YEN"],
    loyaltyProgramName: "PayPay Points",
    loyaltyUnitsLabel: "points",
    loyaltyBalance: 12_000,
    secondaryLoyaltyName: "",
    secondaryLoyaltyBalance: 0,
    groupChatBrand: "LINE",
  },
  kddi_au: {
    id: "kddi_au",
    displayName: "au Lifestyle",
    settlementBrand: "au PAY",
    settlementAdapterId: "coincheck_spot",
    walletAdapterLabel: "au PAY + Coincheck",
    cryptoExchange: "Coincheck",
    txRefPrefix: "coincheck_stub",
    primaryDenomination: "JPYC",
    supportedDenominations: ["JPYC", "BTC", "ETH", "USDC", "YEN"],
    loyaltyProgramName: "au Points",
    loyaltyUnitsLabel: "points",
    loyaltyBalance: 6_400,
    // Ponta is KDDI's ecosystem currency — earned + spent at Lawson,
    // Ponta-partnered restaurants/coupons, on top of au points.
    secondaryLoyaltyName: "Ponta",
    secondaryLoyaltyBalance: 4_200,
    groupChatBrand: "LINE",
  },
};


export interface CarrierProfile {
  id: CarrierId;
  displayName: string;
  settlementBrand: string;
  settlementAdapterId: string;
  walletAdapterLabel: string;
  cryptoExchange: string;
  txRefPrefix: string;
  primaryDenomination: string;
  supportedDenominations: string[];
  loyaltyProgramName: string;
  loyaltyUnitsLabel: string;
  loyaltyBalance: number;
  // Carrier ecosystem extras: KDDI carries Ponta alongside au points;
  // SoftBank pushes group communication through LINE. Both rendered in
  // the chip header and used by demos when relevant.
  secondaryLoyaltyName: string;
  secondaryLoyaltyBalance: number;
  groupChatBrand: string;
}


// One timed event in the script — either a real unified-stream event
// or a demo-only synthetic event the SPA handles specially (e.g.
// chat.user_message to render the prompt the consumer "typed").
export interface DemoFrame {
  delayMs: number;
  event: UnifiedEvent | DemoExtraEvent;
}


export type AgentTone =
  | "your_twin"
  | "partner_twin"
  | "family_twin"
  | "friend_twin"
  | "aristotle"
  | "broker"
  | "negotiator"
  | "provider"
  | "settlement"
  | "system";


export type DemoExtraEvent =
  | { topic_class: "chat"; type: "chat.user_message"; turn_id: string; seq: number; text: string }
  | { topic_class: "chat"; type: "chat.thinking"; turn_id: string; seq: number; text: string }
  | {
      topic_class: "chat"; type: "chat.agent_say";
      turn_id: string; seq: number;
      speaker: string;
      tone: AgentTone;
      text: string;
    }
  | { topic_class: "chat"; type: "chat.assistant_message_start"; turn_id: string; seq: number }
  | { topic_class: "chat"; type: "chat.chunk"; turn_id: string; seq: number; text: string }
  | { topic_class: "chat"; type: "chat.assistant_message_end"; turn_id: string; seq: number }
  | {
      topic_class: "chat"; type: "chat.turn.completed";
      turn_id: string; seq: number; final_text: string;
      usage: Record<string, unknown>; receipt: Record<string, unknown>;
    }
  | { topic_class: "demo"; type: "demo.ending"; phase: "receipt" | "twin_update" | "pivot" };


export interface DemoEndingPanels {
  receipt: {
    receipt_id: string;
    action_summary: string;
    midnight_anchor_hex: string;
    rationale_steps: string[];
    alternatives_considered: string[];
    jfsa_csv_preview: string;
  };
  twinUpdate: {
    category: string;
    key: string;
    old_value: string;
    new_value: string;
    reason: string;
    confidence_change: number;
  };
  pivot: {
    headline: string;
    suggestions: Array<{ vertical: string; description: string; ctaLabel: string }>;
  };
}


export interface DemoScript {
  id: string;
  title: string;
  subtitle: string;
  carrierFit: "both" | "softbank" | "kddi";
  userPrompt: string;
  consumerId: string;
  turnId: string;
  dealId: string;
  totalSeconds: number;
  killerFeatures: string[];
  frames: DemoFrame[];
  endings: DemoEndingPanels;
  // Computed at build time: which DAG nodes this demo actually
  // touches (so unused nodes can be hidden from the graph panel —
  // no point showing Aristotle on demos that don't use it).
  usedNodes: import("./agent-graph-types").NodeId[];
}


// Carrier substitution: replace `{{carrier.field}}` tokens with the
// active CarrierProfile's value. Done shallowly on every string
// value of every frame's event payload + endings.
const TOKEN_RE = /\{\{carrier\.([a-zA-Z_]+)\}\}/g;

function substituteString(value: string, c: CarrierProfile): string {
  return value.replace(TOKEN_RE, (_, key: string) => {
    const v = (c as unknown as Record<string, unknown>)[key];
    return v === undefined ? "" : String(v);
  });
}

function substituteAny(value: unknown, c: CarrierProfile): unknown {
  if (typeof value === "string") return substituteString(value, c);
  if (Array.isArray(value)) return value.map((v) => substituteAny(v, c));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = substituteAny(v, c);
    }
    return out;
  }
  return value;
}


export function compileDemoScript(script: DemoScript, carrier: CarrierProfile): DemoScript {
  return {
    ...script,
    title: substituteString(script.title, carrier),
    subtitle: substituteString(script.subtitle, carrier),
    userPrompt: substituteString(script.userPrompt, carrier),
    killerFeatures: script.killerFeatures.map((s) => substituteString(s, carrier)),
    frames: script.frames.map((f) => ({
      delayMs: f.delayMs,
      event: substituteAny(f.event, carrier) as DemoFrame["event"],
    })),
    endings: substituteAny(script.endings, carrier) as DemoEndingPanels,
  };
}


// ── High-level builder helpers ──────────────────────────────────────


export interface StepBuilder {
  // Chat surface
  userTypes(text: string, delayMs?: number): void;
  thinking(text: string, delayMs?: number): void;
  agentSay(speaker: string, text: string, tone: AgentTone, delayMs?: number): void;
  assistantStreams(chunks: string[], perChunkMs?: number): void;
  assistantDone(finalText: string, delayMs?: number): void;
  // Agent graph
  nodeState(nodeId: NodeId, state: NodeState, label?: string, delayMs?: number): void;
  edgeFire(from: NodeId, to: NodeId, kind: string, payloadPreview?: string, delayMs?: number): void;
  // PACT
  pactBroadcast(verticalLabel: string, providerCount: number, delayMs?: number): void;
  pactBid(providerId: string, providerName: string, priceYen: number, delayMs?: number): void;
  pactDrop(providerId: string, reason: string, delayMs?: number): void;
  pactSealed(providerId: string, providerName: string, priceYen: number, delayMs?: number): void;
  pactNoProposals(reason: string, delayMs?: number): void;
  // Endings
  endingReceipt(delayMs?: number): void;
  endingTwinUpdate(delayMs?: number): void;
  endingPivot(delayMs?: number): void;
  // Raw frame (escape hatch)
  raw(frame: DemoFrame): void;
}


interface ScriptSpec {
  meta: Omit<DemoScript, "frames" | "usedNodes">;
  build: (s: StepBuilder) => void;
}


export function buildDemoScript(spec: ScriptSpec): DemoScript {
  const frames: DemoFrame[] = [];
  let seq = 0;
  const next = () => ++seq;

  const builder: StepBuilder = {
    userTypes(text, delayMs = 200) {
      frames.push({ delayMs, event: {
        topic_class: "chat", type: "chat.user_message",
        turn_id: spec.meta.turnId, seq: next(), text,
      }});
    },
    thinking(text, delayMs = 250) {
      frames.push({ delayMs, event: {
        topic_class: "chat", type: "chat.thinking",
        turn_id: spec.meta.turnId, seq: next(), text,
      }});
    },
    agentSay(speaker, text, tone, delayMs = 450) {
      frames.push({ delayMs, event: {
        topic_class: "chat", type: "chat.agent_say",
        turn_id: spec.meta.turnId, seq: next(),
        speaker, tone, text,
      }});
    },
    assistantStreams(chunks, perChunkMs = 40) {
      frames.push({ delayMs: 200, event: {
        topic_class: "chat", type: "chat.assistant_message_start",
        turn_id: spec.meta.turnId, seq: next(),
      }});
      for (const chunk of chunks) {
        frames.push({ delayMs: perChunkMs, event: {
          topic_class: "chat", type: "chat.chunk",
          turn_id: spec.meta.turnId, seq: next(), text: chunk,
        }});
      }
    },
    assistantDone(finalText, delayMs = 100) {
      frames.push({ delayMs, event: {
        topic_class: "chat", type: "chat.assistant_message_end",
        turn_id: spec.meta.turnId, seq: next(),
      }});
      frames.push({ delayMs: 50, event: {
        topic_class: "chat", type: "chat.turn.completed",
        turn_id: spec.meta.turnId, seq: next(),
        final_text: finalText, usage: {}, receipt: {},
      }});
    },
    nodeState(nodeId, state, label = "", delayMs = 180) {
      const evt: GraphNodeEvent = {
        topic_class: "graph", type: "graph.node_state",
        turn_id: spec.meta.turnId,
        node_id: nodeId, state, label,
        metadata: {}, at: 0,
      };
      frames.push({ delayMs, event: evt });
    },
    edgeFire(from, to, kind, payloadPreview = "", delayMs = 150) {
      const evt: GraphEdgeEvent = {
        topic_class: "graph", type: "graph.edge_fire",
        turn_id: spec.meta.turnId,
        from_node_id: from, to_node_id: to,
        kind, payload_preview: payloadPreview,
        metadata: {}, at: 0,
      };
      frames.push({ delayMs, event: evt });
    },
    pactBroadcast(verticalLabel, providerCount, delayMs = 300) {
      const evt: PactEvent = {
        topic_class: "pact", type: "pact.broadcast",
        deal_id: spec.meta.dealId,
        vertical: verticalLabel,
        matching_provider_count: providerCount,
      };
      frames.push({ delayMs, event: evt });
    },
    pactBid(providerId, providerName, priceYen, delayMs = 250) {
      const evt: PactEvent = {
        topic_class: "pact", type: "pact.bid",
        deal_id: spec.meta.dealId,
        provider_id: providerId,
        provider_name: providerName,
        price_jpy: priceYen,
      };
      frames.push({ delayMs, event: evt });
    },
    pactDrop(providerId, reason, delayMs = 200) {
      const evt: PactEvent = {
        topic_class: "pact", type: "pact.drop",
        deal_id: spec.meta.dealId,
        provider_id: providerId,
        reason,
      };
      frames.push({ delayMs, event: evt });
    },
    pactSealed(providerId, providerName, priceYen, delayMs = 300) {
      const evt: PactEvent = {
        topic_class: "pact", type: "pact.sealed",
        deal_id: spec.meta.dealId,
        provider_id: providerId,
        provider_name: providerName,
        final_price_jpy: priceYen,
      };
      frames.push({ delayMs, event: evt });
    },
    pactNoProposals(reason, delayMs = 200) {
      const evt: PactEvent = {
        topic_class: "pact", type: "pact.no_proposals",
        deal_id: spec.meta.dealId,
        reason,
      };
      frames.push({ delayMs, event: evt });
    },
    endingReceipt(delayMs = 500) {
      frames.push({ delayMs, event: {
        topic_class: "demo", type: "demo.ending", phase: "receipt",
      }});
    },
    endingTwinUpdate(delayMs = 1800) {
      frames.push({ delayMs, event: {
        topic_class: "demo", type: "demo.ending", phase: "twin_update",
      }});
    },
    endingPivot(delayMs = 1800) {
      frames.push({ delayMs, event: {
        topic_class: "demo", type: "demo.ending", phase: "pivot",
      }});
    },
    raw(frame) {
      frames.push(frame);
    },
  };

  spec.build(builder);

  // Compute which DAG nodes this demo actually uses — by walking
  // every node_state + edge_fire event the builder pushed.
  const usedSet = new Set<import("./agent-graph-types").NodeId>();
  for (const f of frames) {
    const ev = f.event;
    if (ev.topic_class === "graph") {
      if (ev.type === "graph.node_state") {
        usedSet.add(ev.node_id);
      } else if (ev.type === "graph.edge_fire") {
        usedSet.add(ev.from_node_id);
        usedSet.add(ev.to_node_id);
      }
    }
  }
  const usedNodes = Array.from(usedSet);

  return { ...spec.meta, frames, usedNodes };
}
